import React, { useState } from 'react'
import PhotoModalComp from './PhotoModalComp';
import IconButton from '@mui/material/IconButton';
import ToastMessage from '../../../../common/ToastMessage';
import { Button } from '@mui/material';
import { TicketImageInterface } from '../../../../models/TicketModule/TicketImage';
import { TicketFeedbackInterface } from '../../../../models/TicketModule/TicketFeedback';

interface PhotoFeedbackCompProps {
    id: string,
    className?: string;
    selectedIndex: number;
    form: TicketFeedbackInterface,
    photos: TicketImageInterface[];
    setSelectedIndex: React.Dispatch<React.SetStateAction<number>>;
    setPhotos: React.Dispatch<React.SetStateAction<TicketImageInterface[]>>;
    setForm: React.Dispatch<React.SetStateAction<TicketFeedbackInterface>>,
}

const validTypes = ['image/jpeg', 'image/png', 'image/gif'];
const acceptString = validTypes.map(type => {
    switch (type) {
        case 'image/jpeg':
            return '.jpg, .jpeg';
        case 'image/png':
            return '.png';
        case 'image/gif':
            return '.gif';
        default:
            return '';
    }
}).join(', ');
const maxSize = 1024 * 1024;

export const PhotoFeedbackComp = ({
    form, className = '', photos, selectedIndex,
    setForm, setPhotos, setSelectedIndex,
}: PhotoFeedbackCompProps) => {
    const [open, setOpen] = useState<boolean>(false);

    const handleOpen = (index: number) => {
        setSelectedIndex(index);
        setOpen(true);
    };

    const handleClose = () => setOpen(false);

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;
        if (files) {
            const newPhotos: TicketImageInterface[] = [];
            const newFormImages: string[] = [];
            let validFileCount = 0;

            Array.from(files).forEach((file) => {
                // Validate file type
                if (!validTypes.includes(file.type)) {
                    ToastMessage.Warn('Only JPG, PNG, and GIF formats are allowed.');
                    return;
                }

                // Validate file size
                if (file.size > maxSize) {
                    ToastMessage.Warn('File size must be less than !MB.');
                    return;
                }

                validFileCount++;

                const reader = new FileReader();
                reader.onloadend = () => {
                    const photoUrl = reader.result as string;

                    if (photos.some(photo => photo.imageUrl === photoUrl)) {
                        ToastMessage.Warn('This photo has already been added.');
                        return;
                    }

                    newPhotos.push({
                        id: Math.random().toString(),
                        ticketId: form.id || '',
                        imageUrl: photoUrl
                    });

                    newFormImages.push(photoUrl);

                    // Update state after processing all valid files
                    if (newPhotos.length === validFileCount) {
                        setPhotos((prevPhotos) => [...prevPhotos, ...newPhotos]);
                        setForm((prevForm) => ({
                            ...prevForm,
                            formImages: prevForm.formImages ? [...prevForm.formImages, ...newFormImages] : newFormImages
                        }));
                    }
                };
                reader.readAsDataURL(file);
            });
        }
    };

    const handleDeletePhoto = (photoUrl: string, id: string) => {
        setPhotos((prevPhotos) => {
            const updatedPhotos = prevPhotos.filter(photo => photo.id !== id);
            const formImages = form.formImages?.filter(item => item !== photoUrl);
            setForm((prevForm) => ({ ...prevForm, formImages }));
            return updatedPhotos;
        });
    };
    return (
        <div className={`d-flex align-items-center gap-2 ${className}`} style={{ overflowX: 'auto' }}>
            {photos.length > 0 && photos.map((photo, index) => (
                <div key={index} style={{ position: 'relative' }}>
                    <Button
                        variant="contained"
                        onClick={() => handleOpen(index)}
                        sx={{
                            padding: 0,
                            width: '90px',
                            height: '60px',
                            color: '#413420',
                            background: '#F3F6F9',
                            transition: 'transform 0.3s ease',
                            '&:hover': {
                                backgroundColor: '#E1E5E8',
                                transform: 'scale(1.1)',
                            },
                        }}
                    >
                        <img
                            src={photo.imageUrl}
                            alt="Uploaded"
                            style={{
                                height: '100%',
                                width: '100%',
                                objectFit: 'cover',
                                borderRadius: '4px',
                            }}
                        />
                    </Button>

                    <IconButton
                        onClick={() => handleDeletePhoto(photo.imageUrl, photo.id)}
                        sx={{
                            top: '1px',
                            right: '1px',
                            width: '10px',
                            height: '10px',
                            fontSize: '8px',
                            color: '#FF0000',
                            position: 'absolute',
                            backgroundColor: 'transparent',
                            '&:hover': {
                                backgroundColor: '#FFDDDD',
                            },
                        }}
                    >
                        ✕
                    </IconButton>
                </div>
            ))}

            <input
                multiple
                type="file"
                id="fileInput"
                accept={acceptString}
                style={{ display: 'none' }}
                onChange={handleFileChange}
            />
            <PhotoModalComp
                open={open}
                photos={photos}
                selectedIndex={selectedIndex}
                handleClose={handleClose}
            />
        </div>
    );
};
export default PhotoFeedbackComp